import type { VirtualElement } from '@floating-ui/react-dom'

// Every computed style that changes where the caret sits along a line of a textarea's text box.
const MIRRORED_PROPERTIES = [
  'padding-inline-start',
  'padding-inline-end',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'letter-spacing',
  'text-transform',
  'word-spacing',
  'text-indent',
  'tab-size',
  'text-align',
  'direction',
] as const

/**
 * How far from the textarea's left edge the caret sits before `index`. A textarea doesn't expose this,
 * so its whole text is laid out again in a hidden copy of its text box, where each word wraps as it does
 * in the textarea, and the caret is measured there.
 */
function getCaretOffsetLeft(textarea: HTMLTextAreaElement, index: number): number {
  const style = window.getComputedStyle(textarea)
  const mirror = document.createElement('div')
  MIRRORED_PROPERTIES.forEach(property => mirror.style.setProperty(property, style.getPropertyValue(property)))
  mirror.style.position = 'absolute'
  mirror.style.visibility = 'hidden'
  mirror.style.boxSizing = 'border-box'
  // clientWidth leaves out the borders and any scrollbar, which the text never wraps into.
  mirror.style.inlineSize = `${textarea.clientWidth}px`
  mirror.style.whiteSpace = 'pre-wrap'
  mirror.style.overflowWrap = 'break-word'
  // The zero-width space gives the caret at the end a character to measure, even after a trailing line break.
  const text = document.createTextNode(`${textarea.value}\u200b`)
  mirror.appendChild(text)
  document.body.appendChild(mirror)
  // The character after the caret, since a collapsed range at a wrapped line's start measures the end of the line above.
  const nextCharacter = document.createRange()
  nextCharacter.setStart(text, index)
  nextCharacter.setEnd(text, index + 1)
  const characterRect = nextCharacter.getClientRects()[0]
  const caretLeft = style.direction === 'rtl' ? characterRect.right : characterRect.left
  const offsetLeft = caretLeft - mirror.getBoundingClientRect().left
  mirror.remove()
  return textarea.clientLeft + offsetLeft
}

/**
 * A floating-ui reference above the caret, from the top of `frame`'s content to the textarea's bottom edge,
 * measured each time floating-ui positions against it. Pinning it to the caret's own line would cover the
 * lines above, and starting it below `frame`'s top would cover what sits above the text, such as chips.
 */
export function createTextareaCaretAnchor(textarea: HTMLTextAreaElement, index: number, frame: Element): VirtualElement {
  return {
    getBoundingClientRect: () => {
      const textareaRect = textarea.getBoundingClientRect()
      const frameTop = frame.getBoundingClientRect().top + frame.clientTop + parseFloat(window.getComputedStyle(frame).paddingBlockStart)
      return new DOMRect(textareaRect.left + getCaretOffsetLeft(textarea, index), frameTop, 0, textareaRect.bottom - frameTop)
    },
    contextElement: textarea,
  }
}
